import { motion } from "framer-motion";
import { ArrowRight, Download } from "lucide-react";

export const Hero = () => {
  const goDownload = () => {
    document.getElementById("download")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center text-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">

      <div className="absolute inset-0 bg-[#040404]" />
      <div className="absolute inset-0 bg-grid opacity-40" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 40%, rgba(255,94,0,0.10), transparent 65%)" }} />
      <div className="absolute inset-0 scanlines pointer-events-none opacity-40" />

      <div className="relative flex flex-col items-center px-4">

        <style>{`@keyframes revealText { to { max-width: 500px; opacity: 1; } }`}</style>

        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="glass-pill glass-sheen flex items-center gap-2 px-4 py-2 mb-6"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-300 font-bold">
            Now available as a free download
          </span>
        </motion.div>

        <div className="flex items-center justify-center mb-5">
          <motion.div initial={{ scale: 0.3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5, delay: 0.3, type: "spring", bounce: 0.5 }} className="w-14 h-14 glass flex items-center justify-center shrink-0 !rounded-2xl">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M13 2L4.5 13.5H11L10 22L20.5 10H14L13 2Z" fill="#ff5e00" />
            </svg>
          </motion.div>
          <div style={{ overflow: "hidden", maxWidth: 0, opacity: 0, animation: "revealText 0.7s cubic-bezier(0.22,1,0.36,1) 0.95s forwards" }}>
            <span className="block pl-4 text-3xl font-black tracking-[0.18em] text-white uppercase whitespace-nowrap" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              Flow Tweaks
            </span>
          </div>
        </div>

        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.7 }} className="text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-black uppercase tracking-tighter leading-[0.88] mb-6" style={{ fontFamily: "'Orbitron', sans-serif" }} data-testid="hero-title">
          Get more frames.
          <br />
          <span className="text-zinc-600">Pay</span>{" "}
          <span className="text-orange-500">less.</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 2.0 }} className="max-w-xl text-zinc-400 text-sm sm:text-base mb-6">
          Download the app, unlock your plan, and apply real performance tweaks yourself —
          no remote sessions, no waiting on Discord. Console plans still get a 1-on-1 walkthrough.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 2.2 }} className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={goDownload}
            className="glass-hover group inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-400 text-black font-black uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-full transition-all hover:shadow-[0_0_30px_rgba(255,94,0,0.5)]"
            data-testid="hero-cta-download"
          >
            <Download className="w-4 h-4" />
            Download Now
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
            Free to download · Plans start at $5
          </span>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 2.5 }} className="flex flex-col items-center gap-2 mt-16">
          <motion.div animate={{ y: [0, 8, 0], opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.6, repeat: Infinity }} className="w-px h-8 bg-orange-500" />
          <span className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Scroll</span>
        </motion.div>

      </div>
    </section>
  );
};
